import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Coffee, Handshake } from "lucide-react";
import { DeptIcon } from "@/components/DeptIcon";
import { HomeNav } from "@/components/clinic-ui/HomeNav";
import { SpinningToken } from "@/components/clinic-ui/SpinningToken";
import { heroVariant } from "@/components/clinic-ui/sunset";

const CSS = `
.hx-img{transform-origin:var(--ox) var(--oy);opacity:0;transform:scale(1.14);transition:opacity 1.6s ease,transform 3.2s cubic-bezier(.2,.7,.1,1)}
.hx-ready .hx-img{opacity:1;transform:scale(1.04)}
.hx-ready .hx-drift{animation:hxDrift 26s ease-in-out 3.2s infinite alternate}
@keyframes hxDrift{from{transform:scale(1) translate3d(0,0,0)}to{transform:scale(1.035) translate3d(-0.6%,-0.4%,0)}}
.hx-entering .hx-img{transform:scale(2.9)!important;transition:transform 1.5s cubic-bezier(.6,0,.3,1),filter 1.5s ease;filter:brightness(1.25)}
.hx-door{position:absolute;top:0;bottom:0;width:50%;background:linear-gradient(90deg,rgba(225,240,245,.55),rgba(255,255,255,.25));backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,.5);opacity:0;pointer-events:none;transition:transform 1s cubic-bezier(.6,0,.3,1) .45s,opacity .4s ease .25s}
.hx-entering .hx-door{opacity:1}
.hx-entering .hx-door-l{transform:translateX(-100%)}
.hx-entering .hx-door-r{transform:translateX(100%)}
.hx-flash{opacity:0;transition:opacity .5s ease 1.15s}
.hx-entering .hx-flash{opacity:1}
.hx-sweep{background:linear-gradient(110deg,transparent 40%,rgba(255,255,255,.14) 50%,transparent 60%);background-size:250% 100%;animation:hxSweep 9s ease-in-out 3s infinite}
@keyframes hxSweep{0%{background-position:120% 0}60%,100%{background-position:-120% 0}}
.hx-ui>*{opacity:0;transform:translateY(18px);transition:opacity .9s ease,transform .9s ease}
.hx-ready .hx-ui>*{opacity:1;transform:none}
.hx-ready .hx-ui>*:nth-child(2){transition-delay:1.5s}.hx-ready .hx-ui>*:nth-child(3){transition-delay:1.7s}
.hx-ready .hx-ui>*:nth-child(4){transition-delay:1.9s}.hx-ready .hx-ui>*:nth-child(5){transition-delay:2.1s}.hx-ready .hx-ui>*:nth-child(1){transition-delay:1.3s}
.hx-nav{opacity:0;transition:opacity 1s ease 1s}.hx-ready .hx-nav{opacity:1}
.hx-entering .hx-ui,.hx-entering .hx-nav{opacity:0!important;transition:opacity .4s ease}
@media (max-width: 767px){.hx-hero{--ox:55%!important;--oy:74%!important;background-image:var(--lqm)!important}}
@media (prefers-reduced-motion: reduce){.hx-img,.hx-ui>*,.hx-nav{transition:none!important;opacity:1!important;transform:none!important}.hx-drift,.hx-sweep{animation:none!important}}
.token-stage{perspective:700px;width:72px;height:72px}@media (min-width:640px){.token-stage{width:112px;height:112px}}
.token-coin{position:relative;width:100%;height:100%;transform-style:preserve-3d;animation:token-spin 6s linear infinite;filter:drop-shadow(0 0 14px rgba(34,211,238,.55)) drop-shadow(0 6px 10px rgba(0,0,0,.45))}
.token-face{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;backface-visibility:hidden;-webkit-backface-visibility:hidden;user-select:none}
.token-back{transform:rotateY(180deg)}
@keyframes token-spin{from{transform:rotateY(0deg)}to{transform:rotateY(360deg)}}
@media (prefers-reduced-motion: reduce){.token-coin{animation:none}}

`;

const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useParallax(ref) {
  useEffect(() => {
    if (reduced() || window.matchMedia("(pointer: coarse)").matches) return undefined;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * -14;
      const y = (e.clientY / window.innerHeight - 0.5) * -8;
      if (ref.current) ref.current.style.translate = `${x}px ${y}px`;
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [ref]);
}

function HeroImage({ layer, v }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div ref={layer} className="absolute -inset-4 transition-[translate] duration-700 ease-out">
      <div className="hx-drift absolute inset-0">
        <picture>
          <source media="(max-width: 767px)" srcSet={`/home/hero-mobile${v}-sm.webp 848w, /home/hero-mobile${v}.webp 1696w`} sizes="100vw" />
          <source srcSet={`/home/hero-desktop${v}-sm.webp 1264w, /home/hero-desktop${v}.webp 2528w`} sizes="100vw" />
          <img src={`/home/hero-desktop${v}.webp`} alt={v ? "Ingresso della clinica Doctor Selvix illuminato di sera, con insegna e piazza pedonale" : "Ingresso della clinica Doctor Selvix con facciata in vetro, insegna e piazza pedonale"} fetchpriority="high" decoding="async"
            onLoad={() => setLoaded(true)} data-testid="home-hero-image" data-variant={v ? "sera" : "giorno"}
            className={`hx-img h-full w-full object-cover ${loaded ? "" : "blur-md"}`} />
        </picture>
      </div>
    </div>
  );
}

function Services({ departments, onEnter }) {
  const navigate = useNavigate();
  return (
    <section id="servizi" className="relative bg-[#0d1517] px-5 py-20 text-white sm:px-10 lg:px-16" data-testid="home-services">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-300">I servizi</p>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold leading-tight sm:text-4xl">Un unico ingresso per consulti, assistenza e documenti</h2>
      <div className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {departments.map((d) => (
          <button key={d.id} data-testid={`home-service-${d.slug}`} onClick={onEnter} className="group flex flex-col gap-3 bg-[#0d1517] p-7 text-left transition-colors hover:bg-[#13201f]">
            <DeptIcon name={d.icon} className="h-6 w-6 text-cyan-300" />
            <span className="font-display text-lg font-semibold">{d.title}</span>
            {d.description && <span className="line-clamp-2 text-sm text-slate-400">{d.description}</span>}
            <span className="mt-auto flex items-center gap-1 text-xs font-semibold text-cyan-300 opacity-70 transition-opacity group-hover:opacity-100">Entra nel reparto <ArrowRight className="h-3.5 w-3.5" /></span>
          </button>
        ))}
      </div>
      <p className="mt-10 text-xs text-slate-500">Doctor Selvix è un assistente basato su AI, non un medico abilitato. In caso di emergenza chiama il 112.</p>
      <footer className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/10 pt-5 text-xs text-slate-500" data-testid="home-footer">
        <span>© {new Date().getFullYear()} Yurax Project srl · P.IVA 12792660966</span>
        <button data-testid="home-legal-link" onClick={() => navigate("/note-legali")} className="font-semibold text-cyan-300 transition-colors hover:text-cyan-200">Note legali</button>
        <span data-testid="home-test-live-notice" className="rounded-md border border-amber-400/70 bg-amber-400/15 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wide text-amber-300">Piattaforma in stato test live. Non abilitata alla vendita o commercio di servizi.</span>
      </footer>
    </section>
  );
}

export function Exterior({ onEnter, departments = [] }) {
  const [stage, setStage] = useState("");
  const navigate = useNavigate();
  const [v] = useState(heroVariant);
  const layer = useRef();
  const timer = useRef(null);
  useParallax(layer);
  useEffect(() => {
    const t = setTimeout(() => setStage("hx-ready"), 60);
    return () => { clearTimeout(t); clearTimeout(timer.current); };
  }, []);
  const enter = () => {
    if (timer.current) return;
    if (reduced()) {
      timer.current = true;
      return onEnter();
    }
    setStage("hx-ready hx-entering");
    timer.current = setTimeout(onEnter, 1650);
  };
  const services = () => document.getElementById("servizi")?.scrollIntoView({ behavior: reduced() ? "auto" : "smooth" });
  return (
    <div className="bg-[#0d1517]" data-testid="clinic-exterior">
      <style>{CSS}</style>
      <section className={`hx-hero relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-[#1b2426] ${stage}`} style={{ "--ox": "60%", "--oy": "62%", "--lqm": `url(/home/hero-mobile${v}-lqip.webp)`, backgroundImage: `url(/home/hero-desktop${v}-lqip.webp)`, backgroundSize: "cover" }} data-testid="home-hero">
        <HeroImage layer={layer} v={v} />
        <div className="hx-sweep pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/10 to-slate-950/75" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(2,6,23,.45),transparent_65%)]" />
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-slate-950/70 via-slate-950/25 to-transparent lg:block" />
        <div className="hx-door hx-door-l left-0" /><div className="hx-door hx-door-r right-0" />
        <div className="hx-flash pointer-events-none absolute inset-0 bg-[#f3f7f8]" />
        <HomeNav onClinic={enter} onServices={services} />
        <SpinningToken />
        <div className="hx-ui absolute inset-x-0 top-1/2 z-20 mx-auto flex max-w-3xl -translate-y-1/2 flex-col items-center px-5 text-center text-white lg:inset-x-auto lg:left-14 lg:top-auto lg:bottom-20 lg:mx-0 lg:max-w-xl lg:translate-y-0 lg:items-start lg:px-0 lg:text-left">
          <h1 className="sr-only">Doctor Selvix – clinica virtuale</h1>
          <p className="max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">Entra in una nuova dimensione dell'assistenza sanitaria. Professionisti, consulti online, servizi domiciliari e innovazione digitale in un unico ambiente.</p>
          <div className="mt-8 flex flex-col flex-wrap justify-center gap-3 sm:flex-row lg:grid lg:w-max lg:grid-cols-2 lg:justify-start">
            <button data-testid="enter-clinic-btn" onClick={enter} className="group flex items-center justify-center gap-3 rounded-full border border-red-300/60 bg-red-600 px-8 py-4 text-sm font-bold tracking-[0.22em] text-white shadow-[0_10px_40px_-10px_rgba(220,38,38,.7)] backdrop-blur-md transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-red-500 sm:basis-full lg:col-span-2 lg:justify-self-center">
              ENTRA NELLA CLINICA <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="enter-cafe-btn" onClick={() => navigate("/cafe")} className="flex items-center justify-center gap-2 rounded-full border border-pink-300/60 bg-slate-950/40 px-8 py-4 text-sm font-bold tracking-[0.22em] text-pink-100 shadow-[0_0_24px_-6px_rgba(255,79,163,.8)] backdrop-blur-md transition-colors hover:bg-slate-950/60"><Coffee className="h-4 w-4" />ENTRA NEL SELVIX CAFÉ</button>
            <button data-testid="home-partners-btn" onClick={() => navigate("/partner")} className="flex items-center justify-center gap-2 rounded-full border border-cyan-300/60 bg-slate-950/40 px-8 py-4 text-sm font-bold tracking-[0.22em] text-cyan-100 backdrop-blur-md transition-colors hover:bg-slate-950/60"><Handshake className="h-4 w-4" />I NOSTRI PARTNER</button>
          </div>
          <p className="mt-6 rounded-full bg-slate-950/35 px-4 py-1.5 text-[11px] text-white/80 backdrop-blur-sm" data-testid="home-ai-disclaimer">Doctor Selvix è un assistente basato su AI, non un medico abilitato. Emergenze: <a href="tel:112" className="font-bold text-white underline">112</a>.</p>
        </div>
        <button onClick={services} aria-label="Scorri ai servizi" data-testid="home-scroll-cue" className="absolute bottom-5 left-1/2 z-20 -translate-x-1/2 text-white/70 transition-colors hover:text-white"><ChevronDown className="h-6 w-6 animate-bounce" /></button>
      </section>
      <Services departments={departments} onEnter={enter} />
    </div>
  );
}
